import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AppButton, StickyHeaderNavigation } from '@/components'
import { Avatar } from '@/components/Avatar'
import { useSplitTransactionStore } from '@/store/useSplitTransactionStore'
import { useHandleSplitRequests } from '@/features/send-request/hooks/useHandleSplitRequests'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'
import { getAvatarProps } from '@/utils/avatarUtils'

const { formatCryptoAmount, formatFiatCurrency } = currencyUtils

export const SplitRequestConfirmScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()

  const transaction = useSplitTransactionStore((s) => s.transaction)
  const selectedRecipients = useSplitTransactionStore(
    (s) => s.selectedRecipients,
  )
  const getAmountPerPerson = useSplitTransactionStore(
    (s) => s.getAmountPerPerson,
  )
  const getInitiatorAmount = useSplitTransactionStore(
    (s) => s.getInitiatorAmount,
  )

  // Redirect if no transaction or recipients
  useEffect(() => {
    if (!transaction || selectedRecipients.length === 0) {
      navigate('/activity', { replace: true })
    }
  }, [transaction, selectedRecipients.length, navigate])

  // Use the split requests hook
  const { handleSendSplitRequests, isLoading } = useHandleSplitRequests()

  const handleConfirm = async () => {
    await handleSendSplitRequests()
    // Don't clear split here - let the success screen do it
    // This allows the success screen to access the split data if needed
  }

  const handleBackPress = () => {
    navigate(-1)
  }

  if (!transaction || selectedRecipients.length === 0) {
    return null
  }

  const amountPerPerson = getAmountPerPerson()
  const initiatorAmount = getInitiatorAmount()

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        height: '100vh',
      }}
    >
      <StickyHeaderNavigation
        title={t('translation:confirmSplit', { defaultValue: 'Confirm split' })}
        onBackPress={handleBackPress}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        pt={0}
        pb={0}
        style={{
          paddingTop: `${offsets.top + 56}px`,
          overflowY: 'auto',
          paddingBottom: '120px',
        }}
      >
        {/* Header */}

        {/* Transaction Info */}
        <BaseView
          bgColor="background.cardItemRoot"
          style={{
            padding: '20px',
            borderRadius: '16px',
            marginTop: '30px',
            marginBottom: '28px',
          }}
        >
          <BaseText
            fontSize={14}
            fontFamily="medium"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: '8px' }}
          >
            {t('translation:originalAmount', {
              defaultValue: 'Original amount',
            })}
          </BaseText>
          <BaseText
            fontSize={28}
            fontFamily="bold"
            color="label.default"
            textAlign="center"
            style={{ marginBottom: '4px' }}
          >
            {transaction.amount.amount} {transaction.amount.currency}
          </BaseText>
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: '16px' }}
          >
            ≈
            {formatFiatCurrency({
              fiatCode: transaction.amount.fiat_currency,
              value: Number(transaction.amount.fiat_amount),
              showDecimals: true,
            })}
          </BaseText>

          {/* Split Preview */}
          {
            <>
              <BaseText
                fontSize={14}
                fontFamily="medium"
                color="label.description"
                textAlign="left"
                style={{ marginBottom: '12px' }}
              >
                {t('translation:splitPreview', {
                  defaultValue: 'Split preview:',
                })}
              </BaseText>

              <BaseView gap={8}>
                <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {t('translation:yourShare', { defaultValue: 'Your share' })}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {formatCryptoAmount({
                      value: initiatorAmount,
                      cryptoCode: transaction.amount.currency,
                      showDecimals: true,
                      showCryptoCode: false,
                    })}{' '}
                    {transaction.amount.currency}
                  </BaseText>
                </BaseView>

                <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {selectedRecipients.length > 0
                      ? selectedRecipients.length > 1 && selectedRecipients[0]
                        ? t('translation:perPerson', {
                            defaultValue: 'Each person pays',
                          })
                        : `${selectedRecipients[0].name} ${t(
                            'translation:personPays',
                            {
                              defaultValue: 'pays',
                            },
                          )}`
                      : '...'}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {formatCryptoAmount({
                      value: amountPerPerson,
                      cryptoCode: transaction.amount.currency,
                      showDecimals: true,
                      showCryptoCode: false,
                    })}{' '}
                    {transaction.amount.currency}
                  </BaseText>
                </BaseView>

                {/* <BaseView row justify="space-between">
                  <BaseText fontSize={14} color="label.textTextInput">
                    {t('translation:totalPeople', {
                      defaultValue: 'Selected',
                    })}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {selectedRecipients.length > 0 ? totalPeople - 1 : '...'}
                  </BaseText>
                </BaseView> */}
              </BaseView>
            </>
          }
        </BaseView>

        {/* Recipients requesting from */}
        <BaseView mb={24}>
          <BaseText
            fontSize={16}
            fontFamily="semibold"
            color="label.textTextInput"
            style={{ marginBottom: '12px' }}
          >
            {t('translation:requestingFrom', {
              defaultValue: 'Requesting from',
            })}
          </BaseText>

          <BaseView gap={12}>
            {selectedRecipients.map((recipient, index) => {
              const avatarProps = getAvatarProps({
                first_name: recipient.name.split(' ')[0],
                last_name: recipient.name.split(' ')[1] || '',
                avatar_path: recipient.avatar,
                avatarBg: recipient.avatarBg,
                id: recipient.id,
              })

              return (
                <BaseView
                  key={`recipient-${recipient.id}-${index}`}
                  bgColor="background.cardItemRoot"
                  row
                  align="center"
                  gap={12}
                  style={{
                    padding: '12px',
                    borderRadius: '24px',
                    minHeight: '72px',
                  }}
                >
                  <Avatar
                    size={48}
                    initials={avatarProps.initials}
                    imageUrl={avatarProps.avatarUrl}
                    gradientColors={avatarProps.backgroundGradient}
                    fullSize={false}
                  />
                  <BaseView flex={1}>
                    <BaseText
                      fontSize={16}
                      fontFamily="semibold"
                      color="label.textTextInput"
                    >
                      {recipient.name}
                    </BaseText>
                    {recipient.username && (
                      <BaseText
                        fontSize={14}
                        fontFamily="regular"
                        color="label.primary"
                      >
                        @{recipient.username}
                      </BaseText>
                    )}
                  </BaseView>
                  <BaseView align="flex-end">
                    <BaseText
                      fontSize={16}
                      fontFamily="semibold"
                      color="label.default"
                    >
                      {formatCryptoAmount({
                        value: amountPerPerson,
                        cryptoCode: transaction.amount.currency,
                        showDecimals: true,
                        showCryptoCode: false,
                      })}
                    </BaseText>
                    <BaseText
                      fontSize={12}
                      fontFamily="regular"
                      color="label.description"
                    >
                      {transaction.amount.currency}
                    </BaseText>
                  </BaseView>
                </BaseView>
              )
            })}
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Fixed Bottom Button */}
      <BaseView
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          paddingBottom: `${offsets.bottom + 16}px`,
          backgroundColor: '#131415',
        }}
      >
        <AppButton
          label={t('translation:sendSplitRequests', {
            count: selectedRecipients.length,
            defaultValue: `Send ${selectedRecipients.length} split requests`,
          })}
          onPress={handleConfirm}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </BaseView>
    </BaseView>
  )
}

export default SplitRequestConfirmScreen
